import { useQuery } from '@apollo/client'
import {
  ActionIcon,
  Box,
  Divider,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core'
import { useDisclosure, useHover } from '@mantine/hooks'
import { IconLockFilled, IconLogin2, IconRss, IconWorld } from '@tabler/icons-react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import itemIconBlack from '@/assets/icons/items_black.svg'
import itemIconBlue from '@/assets/icons/items_blue.svg'
import itemIconGreen from '@/assets/icons/items_green.svg'
import itemIconWhite from '@/assets/icons/items_white.svg'
import RootButton from '@/components/Buttons/RootButton'
import Web3Button from '@/components/Buttons/Web3Button'
import ParseRichText from '@/components/Contentful/ParseRichText'
import CollectionsCard from '@/components/Items/CollectionsCard'
import ItemsCard from '@/components/Items/ItemsCard'
import IconWrapper from '@/components/MediaWrappers/IconWrapper'
import ImageWrapper from '@/components/MediaWrappers/ImageWrapper'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import optionMenuClasses from '@/components/OptionsMenu.module.css'
import { useAuth } from '@/plugins/auth'
import { saveLocation } from '@/plugins/auth/RouteRedirectors/utils'
import { RichText } from '@/plugins/contentful/api'
import { GetCollectionsByIds, QUERY_GET_COLLECTIONS_BY_IDS } from '@/schemas/collections.schema'
import {
  GetContentAuth,
  GetItemsByIds,
  QUERY_GET_CONTENT_AUTH,
  QUERY_GET_ITEMS_BY_IDS,
} from '@/schemas/tokens.schema'
import { replaceYoutubeLinkEndpoint } from '@/utils/utils'

import PostHeader from './PostHeader'
import classes from './style.module.css'

interface NftsRequiredListProps {
  collectionsIds: string[]
  andItemsIds: string[]
  orItemsIds: string[]
}

const NftsRequiredList: React.FC<NftsRequiredListProps> = ({
  collectionsIds,
  andItemsIds,
  orItemsIds,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const { data } = useQuery<GetCollectionsByIds>(QUERY_GET_COLLECTIONS_BY_IDS, {
    variables: {
      ids: collectionsIds,
    },
  })

  // get nfts by ids
  const { data: items } = useQuery<GetItemsByIds>(QUERY_GET_ITEMS_BY_IDS, {
    variables: {
      ids: andItemsIds,
    },
    skip: andItemsIds.length === 0,
  })

  const { data: orItems } = useQuery<GetItemsByIds>(QUERY_GET_ITEMS_BY_IDS, {
    variables: {
      ids: orItemsIds,
    },
    skip: orItemsIds.length === 0,
  })

  const sortedData = collectionsIds
    // map the ids to the nfts
    .map(id => data?.collection.find(nft => nft.id === id))
    // filter out the undefined values
    .filter(nft => nft !== undefined)

  const sortedDataItems = andItemsIds
    // map the ids to the nfts
    .map(id => items?.tokens.find(nft => nft.id === id))
    // filter out the undefined values
    .filter(nft => nft !== undefined)

  const sortedDataOrItems = orItemsIds
    // map the ids to the nfts
    .map(id => orItems?.tokens.find(nft => nft.id === id))
    // filter out the undefined values
    .filter(nft => nft !== undefined)
  return (
    <Stack>
      {collectionsIds.length > 0 && (
        <Title order={4}>Have an item from the following collections</Title>
      )}
      <SimpleGrid cols={2}>
        {sortedData.map(collection => (
          <CollectionsCard
            key={collection.id}
            collection={collection}
            parentRef={parentRef}
            showCreator={false}
            showType={false}
            dynamic={false}
          />
        ))}
      </SimpleGrid>
      {andItemsIds.length > 0 && <Title order={4}>Have all of these items</Title>}
      <SimpleGrid cols={2}>
        {sortedDataItems.map(item => (
          <ItemsCard key={item.id} elem={item} parentRef={parentRef} showCreator={false} />
        ))}
      </SimpleGrid>
      {orItemsIds.length > 0 && <Title order={4}>Have one of these items</Title>}
      <SimpleGrid cols={2}>
        {sortedDataOrItems.map(item => (
          <ItemsCard key={item.id} elem={item} parentRef={parentRef} showCreator={false} />
        ))}
      </SimpleGrid>
    </Stack>
  )
}

interface ContentProps {
  item: any
  index: number
  hasJoined: boolean
  handleJoinSpace?: () => void
}

interface AccessSpecs {
  display: boolean
  approved: boolean
  requirement: string
  getAccess: {
    tooltip: string | React.ReactNode
    icon: React.ReactNode
  }
  hasAccess: {
    tooltip: string | React.ReactNode
    icon: React.ReactNode
  }
  cta?: React.ReactNode
}

export interface AccessConditions {
  open: AccessSpecs
  login: AccessSpecs
  join: AccessSpecs
  nfts: AccessSpecs
}

const Content: React.FC<ContentProps> = ({ item, index, hasJoined, handleJoinSpace }) => {
  const theme = useMantineTheme()
  const { status, connect, address } = useAuth()
  const navigate = useNavigate()
  const {
    timestamp,
    description,
    image,
    videoLink,
    thumbnailLock,
    buttonText,
    buttonLink,
    contentAuth,
  } = item.fields || {}
  const { file, title } = image?.fields || {}
  const { ref } = useHover()
  const [opened, { open, close }] = useDisclosure(false)
  const [lockHovered, setLockHovered] = useState(false)

  const [checkFlags, setCheckFlags] = useState({
    anyFromCollection: false,
    allFromTokenList: false,
    someFromTokenList: false,
  })

  const variables = useMemo(() => {
    const baseConditions = [
      { account: { _eq: address ? address.toLowerCase() : '' } },
      { token: { collection: { collection_type: { _neq: 'ERC20' } } } },
    ]

    // Function to generate conditions based on contentAuth's "nft" structure
    const generateNftConditions = (nft: any) => {
      const collectionConditions: any = []
      const andConditions: any = []
      const orConditions: any = []

      if (!nft) return {}

      // 1. Check if has collectionIds array, if so, user must have at least one token from that collection (assuming tokenIds array is empty)
      const anyFromCollection =
        nft.collectionIds && nft.collectionIds.length > 0 && nft.tokenIds?.length === 0
      if (anyFromCollection) {
        collectionConditions.push({
          token: { collection: { id: { _in: nft.collectionIds } } },
        })
      }

      // 2. Check if has tokenIds array, if so, user must have all or some of the tokenIds specified in the array from that collection (depending on the condition)
      const allFromTokenList = nft.tokenIds && nft.tokenIds.length > 0 && nft.tokenIds[0].length > 0
      if (allFromTokenList) {
        nft.tokenIds[0].forEach((tokenId: string) => {
          andConditions.push({
            token: { id: { _eq: tokenId } },
          })
        })
      }

      const someFromTokenList =
        nft.tokenIds && nft.tokenIds.length > 0 && nft.tokenIds[1].length > 0
      if (someFromTokenList) {
        nft.tokenIds[1].forEach((tokenId: string) => {
          orConditions.push({
            token: { id: { _eq: tokenId } },
          })
        })
      }

      setCheckFlags({
        anyFromCollection,
        allFromTokenList,
        someFromTokenList,
      })

      return {
        collection: collectionConditions,
        and: andConditions,
        or: orConditions,
      }
    }

    // Add the generated conditions to the base conditions
    const contentAuthConditions = contentAuth?.nft ? generateNftConditions(contentAuth.nft) : {}

    // create a false condition to avoid returning all items
    const falseCondition = {
      _and: [{ id: { _eq: 'false' } }],
    }

    const whereCollectionConditions =
      contentAuthConditions.collection.length > 0
        ? {
            _and: [...baseConditions, ...contentAuthConditions.collection].filter(Boolean),
          }
        : falseCondition

    const whereAndConditions =
      contentAuthConditions.and.length > 0
        ? {
            _and: [...baseConditions, ...contentAuthConditions.and].filter(Boolean),
          }
        : falseCondition

    const whereOrConditions =
      contentAuthConditions.or.length > 0
        ? {
            _and: [...baseConditions].filter(Boolean),
            _or: [...contentAuthConditions.or].filter(Boolean),
          }
        : falseCondition

    return {
      whereCollection: whereCollectionConditions,
      whereAnd: whereAndConditions,
      whereOr: whereOrConditions,
    }
  }, [address, contentAuth])

  const { loading, error, data, refetch } = useQuery<GetContentAuth>(QUERY_GET_CONTENT_AUTH, {
    variables,
  })

  const hasNftsRequired = useMemo(() => {
    if (!data || loading || error) return false
    const nAndConditions =
      (contentAuth?.nft?.tokenIds?.length !== 0 && contentAuth?.nft?.tokenIds?.[0].length) || 0

    const hasTokenFromCollection = checkFlags.anyFromCollection ? data.collection.length > 0 : true

    const hasAllTokensFromList = checkFlags.allFromTokenList
      ? nAndConditions === data.and.length
      : true

    const hasSomeTokensFromList = checkFlags.someFromTokenList ? data.or.length > 0 : true

    return hasTokenFromCollection && hasAllTokensFromList && hasSomeTokensFromList
  }, [data, loading, error, contentAuth, checkFlags])

  const hasAccess = useMemo(() => {
    // 1. Everyone can access content, logged in or not
    if (contentAuth.method === 'open') {
      return true
    }
    // 2. User is logged in and can access content
    if (contentAuth.method === 'loggedIn') {
      return (status === 'connected' && hasNftsRequired) || false
    }
    // 3. User is logged in and has to have joined the space
    if (contentAuth.method === 'joined') {
      return status === 'connected' && hasJoined && hasNftsRequired
    }

    return false
  }, [contentAuth, status, hasNftsRequired, hasJoined])

  const accessConditions: AccessConditions = useMemo(() => {
    const access = {
      open: {
        display: contentAuth?.method === 'open',
        approved: true,
        requirement: 'none',
        getAccess: {
          tooltip: 'No requirements to unlock this content',
          icon: <IconWorld size={14} color={theme.colors.title[0]} />,
        },
        hasAccess: {
          tooltip: 'Everyone has access to this content',
          icon: <IconWorld size={14} color={theme.colors.l1Primary[0]} />,
        },
      },
      login: {
        display:
          contentAuth?.method === 'loggedIn' ||
          contentAuth?.method === 'joined' ||
          (contentAuth?.nft && Object.keys(contentAuth.nft).length > 0),
        approved: status === 'connected' && !!address,
        requirement: 'Signed in',
        getAccess: {
          tooltip: 'Sign in to unlock this content',
          icon: <IconLogin2 size={14} color={theme.colors.title[0]} />,
        },
        hasAccess: {
          tooltip: 'You are already signed in',
          icon: <IconLogin2 size={14} color={theme.colors.l1Primary[0]} />,
        },
        cta:
          status === 'connected' ? (
            <RootButton style1={false} disabled>
              Signed In
            </RootButton>
          ) : (
            <Web3Button style1={false} />
          ),
      },
      join: {
        display: contentAuth?.method === 'joined',
        approved: status === 'connected' && hasJoined,
        requirement: 'Space membership',
        getAccess: {
          tooltip: 'Join the space to unlock this content',
          icon: <IconRss size={14} color={theme.colors.title[0]} />,
        },
        hasAccess: {
          tooltip: 'You are a member of this space',
          icon: <IconRss size={14} color={theme.colors.l1Primary[0]} />,
        },
        cta: hasJoined ? (
          <RootButton style1={false} disabled>
            Joined
          </RootButton>
        ) : (
          <Tooltip
            label="You need to be signed in first to join the space"
            position="top"
            style={{ display: status !== 'connected' ? 'block' : 'none' }}
          >
            <Box>
              <RootButton
                style1={false}
                onClick={handleJoinSpace}
                disabled={status !== 'connected'}
              >
                Join Space
              </RootButton>
            </Box>
          </Tooltip>
        ),
      },
      nfts: {
        display:
          contentAuth?.method !== 'open' &&
          contentAuth?.nft &&
          Object.keys(contentAuth.nft).length > 0,
        approved: status === 'connected' && hasNftsRequired,
        requirement: 'Items required',
        getAccess: {
          tooltip:
            'Own specific items to unlock this content. Click on the unlock button to find out more',
          icon: (
            <IconWrapper
              iconDark={itemIconWhite}
              iconLight={itemIconBlack}
              width="13px"
              height="13px"
            />
          ),
        },
        hasAccess: {
          tooltip: 'You own the required items',
          icon: (
            <IconWrapper
              iconDark={itemIconGreen}
              iconLight={itemIconBlue}
              width="13px"
              height="13px"
            />
          ),
        },
        cta: (
          <NftsRequiredList
            collectionsIds={contentAuth?.nft.collectionIds || []}
            andItemsIds={contentAuth?.nft.tokenIds?.and || []}
            orItemsIds={contentAuth?.nft.tokenIds?.or || []}
          />
        ),
      },
    }

    return access
  }, [address, hasJoined, data, status, hasNftsRequired, contentAuth])

  const handleNavigation = useCallback(() => {
    if (status !== 'connected' && connect) {
      saveLocation(buttonLink)
      connect()
    } else if (buttonLink.startsWith('/')) {
      saveLocation(buttonLink)
      navigate(buttonLink)
    } else {
      window.open(buttonLink, '_blank')
    }
  }, [status, buttonLink, navigate])

  const descriptionWithoutAccess = useMemo(() => {
    // rnadomize wich sentence is returned from a list of phrases
    const randomPhrase = (phrases: string[]) => phrases[Math.floor(Math.random() * phrases.length)]

    const phrases = [
      'You need to unlock this content to view the description.',
      'Don`t try to be sneaky! You need to unlock this content to view the description.',
      'You thought that was going to work? 😅 The only way you are going to see this content is by following the necessary requirements!',
      'You need to unlock this content to view the description. No shortcuts here!',
      'You thought you were gaining access to premium content without following the rules? 😏 Sneaky sneaky, don`t be like that. Try to follow the requirements and unlock this content!',
    ]

    return randomPhrase(phrases)
  }, [])

  return (
    <Stack key={item.sys.id} className={classes.content} style={{ position: 'relative' }} ref={ref}>
      {index !== 0 && <Divider />}
      <PostHeader timestamp={timestamp || item.sys.updatedAt} accessConditions={accessConditions} />
      <Box
        style={{
          filter: hasAccess ? 'none' : 'blur(5px)',
        }}
      >
        {hasAccess && description && <ParseRichText jsonData={description as RichText} />}
        {!hasAccess && description && (
          <Text style={{ color: theme.colors.textAndIcon[0] }}>{descriptionWithoutAccess}</Text>
        )}
      </Box>
      {buttonText && buttonLink && (
        <RootButton blurOnDisabled disabled={!hasAccess} onClick={handleNavigation}>
          {buttonText}
        </RootButton>
      )}
      {image && (
        <ImageWrapper
          src={file.url}
          alt={title}
          style={{
            width: '100%',
            borderRadius: '10px',
          }}
        />
      )}
      {videoLink &&
        (hasAccess ? (
          <iframe
            style={{
              borderRadius: '10px',
              border: 'none',
              height: '430px',
              backgroundColor: theme.colors.background3[0],
            }}
            src={replaceYoutubeLinkEndpoint(videoLink)}
            allow="autoplay; encrypted-media"
            title="video"
            allowFullScreen
            color="white"
          />
        ) : (
          <Stack style={{ position: 'relative' }}>
            <ImageWrapper
              src={thumbnailLock.fields.file.url}
              alt={title}
              style={{
                width: '100%',
                height: '430px',
                borderRadius: '10px',
              }}
            />
          </Stack>
        ))}
      {!hasAccess && (
        <Stack
          style={{
            position: 'absolute',
            top: '65%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <ActionIcon
            className={optionMenuClasses.actionIcon}
            style={{ padding: '10px', width: '50px', height: '50px' }}
            onMouseEnter={() => setLockHovered(true)}
            onMouseLeave={() => setLockHovered(false)}
            onClick={() => {
              refetch()
              open()
            }}
          >
            <IconLockFilled
              size="34px"
              color={lockHovered ? theme.colors.background[0] : theme.colors.title[0]}
            />
          </ActionIcon>
        </Stack>
      )}
      <ModalSkeleton size="500px" opened={opened} onClose={close} title="Access Requirements">
        <Stack style={{ gap: '10px', alignItems: 'center' }}>
          {Object.keys(accessConditions)
            .filter(key => key !== 'open')
            .map(
              key =>
                accessConditions[key as keyof AccessConditions].display && (
                  <Stack
                    key={key}
                    style={{
                      alignItems: 'center',
                      backgroundColor: theme.colors.background3[0],
                      padding: '16px 12px',
                      borderRadius: '12px',
                      width: '100%',
                    }}
                  >
                    <Stack
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Stack style={{ flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                        {accessConditions[key as keyof AccessConditions].approved
                          ? accessConditions[key as keyof AccessConditions]?.hasAccess.icon
                          : accessConditions[key as keyof AccessConditions]?.getAccess.icon}
                        <Text>{accessConditions[key as keyof AccessConditions].requirement}</Text>
                      </Stack>
                      {key !== 'nfts' && accessConditions[key as keyof AccessConditions].cta}
                    </Stack>
                    {key === 'nfts' && accessConditions[key as keyof AccessConditions].cta}
                  </Stack>
                )
            )}
        </Stack>
      </ModalSkeleton>
    </Stack>
  )
}

export default Content
